/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { BookingStatus } from '@/types';

// Tailwind class merge utility
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ── Date formatting utilities (browser local timezone) ───────────────
// THE RULE: the database stores UTC, the screen shows the reader's own clock.
// Backend serializes all DateTimes as UTC (with 'Z' suffix). We parse them
// as UTC and display them in the user's local timezone (whatever the browser
// reports). No hardcoded timezone — each user sees their own wall-clock time.
//
// Render through these helpers rather than `new Date(iso).toLocaleString()`.
// The bare form is right only while every payload carries its 'Z': the day an
// endpoint serializes a DateTime without one, the browser reads it as local
// time and the screen is off by the reader's offset with nothing to show for
// it. parseUtc closes that gap in one place.
//
// TWO deliberate exceptions, and only these:
//   • A CALENDAR DATE — date of birth, a licence's expiry, a toll's date
//     column (`date` in SQL) — is not an instant. It is stored as midnight and
//     rendering it on a clock west of Greenwich walks it back a day, so it is
//     formatted in UTC: formatDateUtc / formatDateShortUtc.
//   • A TOLL CROSSING belongs to the booth it happened at and is told in the
//     agency's zone, which is what the printed invoice does too: the *InZone
//     helpers below.

/**
 * Parse an ISO string from the API as UTC and return a Date object.
 * If the string already contains timezone info (Z or ±HH:MM), it is respected.
 * If the string has no timezone, it's assumed to be UTC (backend convention).
 */
export function parseUtc(date: string | Date | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
  // If the string lacks explicit timezone info, treat as UTC.
  const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(date);
  const d = new Date(hasTz ? date : date + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

/**
 * The way back out of a form: turn what `<input type="datetime-local">` (or a date input) holds
 * into the UTC instant the API stores.
 *
 * Note the asymmetry with parseUtc, and that it is not an inconsistency. A naive string FROM THE
 * SERVER is UTC; a naive string FROM A FORM is the wall clock the user just typed on their own
 * screen. Sending the latter as-is is how a 9 PM pickup reached the database as 9 PM UTC — five
 * hours early, on a booking the overlap check had already compared against real UTC windows.
 *
 * A bare `YYYY-MM-DD` is read as local midnight (what the user picked on their calendar), not as
 * the UTC midnight `new Date` would give it. A value that already names an instant is passed
 * through. Anything unparseable comes back as '' rather than "Invalid Date".
 */
export function datetimeLocalToUtcIso(value: string | Date | null | undefined): string {
  if (!value) return '';
  if (value instanceof Date) return isNaN(value.getTime()) ? '' : value.toISOString();
  const s = value.trim();
  if (!s) return '';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00` : s);
  return isNaN(d.getTime()) ? '' : d.toISOString();
}

/**
 * Convert a Date into the `YYYY-MM-DDTHH:mm` string format expected by
 * `<input type="datetime-local">` — local wall-clock, no timezone suffix.
 */
export function toDatetimeLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Mar 10, 2026, 3:45 PM" — date + time in browser local timezone */
export function formatDateTime(date: string | Date | null | undefined): string {
  const d = parseUtc(date);
  if (!d) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(d);
}

/** "Mar 10, 2026" — date only in browser local timezone */
export function formatDate(date: string | Date | null | undefined): string {
  const d = parseUtc(date);
  if (!d) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(d);
}

/** "Mar 10, 2026" — date only in UTC (for date-only fields like DOB, license exp that shouldn't shift by timezone) */
export function formatDateUtc(date: string | Date | null | undefined): string {
  const d = parseUtc(date);
  if (!d) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeZone: 'UTC',
  }).format(d);
}

/** "03/10/2026" — short numeric date in browser local timezone */
export function formatDateShort(date: string | Date | null | undefined): string {
  const d = parseUtc(date);
  if (!d) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: '2-digit', day: '2-digit', year: 'numeric',
  }).format(d);
}

/** "03/10/2026" — short date formatted in UTC (for date-only fields like DOB, license expiration that shouldn't shift by timezone) */
export function formatDateShortUtc(date: string | Date | null | undefined): string {
  const d = parseUtc(date);
  if (!d) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: '2-digit', day: '2-digit', year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/** "3:45 PM" — time only in browser local timezone */
export function formatTimeOnly(date: string | Date | null | undefined): string {
  const d = parseUtc(date);
  if (!d) return '';
  return new Intl.DateTimeFormat(undefined, {
    timeStyle: 'short',
  }).format(d);
}

/** "15:45:08" — time only, 24h with seconds, in browser local timezone (grids that show a scrape instant) */
export function formatTimeWithSeconds(date: string | Date | null | undefined): string {
  const d = parseUtc(date);
  if (!d) return '';
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(d);
}

// ── Toll times: the road's clock, not the reader's ───────────────────
// A toll crossing belongs to the booth it happened at, and the agency's own statement tells it in that
// booth's timezone. Everything else on screen is the owner's own schedule and stays in browser-local, but
// a crossing rendered on a different clock than the invoice prints it on is how the page came to say
// 4:45 PM while the PDF said 20:45 — the same instant, told twice, once outside the rental it belongs to.
// The zone comes from the backend with the row (TollTimeZone.ResolveId); this is only the rendering.

/** Eastern — what the backend falls back to, mirrored so the client never silently renders in UTC. */
export const DEFAULT_TOLL_TIME_ZONE = 'America/New_York';

const tollZone = (zone?: string | null): string => zone?.trim() || DEFAULT_TOLL_TIME_ZONE;

/** Format a UTC instant in an explicit IANA zone; an unusable zone falls back rather than throwing. */
function inZone(
  date: string | Date | null | undefined,
  zone: string | null | undefined,
  opts: Intl.DateTimeFormatOptions,
): string {
  const d = parseUtc(date);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: tollZone(zone) }).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: DEFAULT_TOLL_TIME_ZONE }).format(d);
  }
}

/** "Sep 8, 2026, 4:45 PM" at the booth. */
export function formatDateTimeInZone(date: string | Date | null | undefined, zone?: string | null): string {
  return inZone(date, zone, { dateStyle: 'medium', timeStyle: 'short' });
}

/** "09/08/2026" at the booth — the date an invoice line is printed under. */
export function formatDateNumericInZone(date: string | Date | null | undefined, zone?: string | null): string {
  return inZone(date, zone, { month: '2-digit', day: '2-digit', year: 'numeric' });
}

/** "16:45:00" at the booth, 24h — the form the invoice itemisation prints. */
export function formatTime24InZone(date: string | Date | null | undefined, zone?: string | null): string {
  return inZone(date, zone, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

/**
 * "EDT" / "PST" — the label that makes a printed time unambiguous. Read off the formatter rather than
 * mapped by hand so it follows the zone's own DST rules.
 */
export function tollZoneAbbr(date: string | Date | null | undefined, zone?: string | null): string {
  const d = parseUtc(date);
  if (!d) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tollZone(zone), timeZoneName: 'short',
    }).formatToParts(d);
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

// Format currency
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount ?? 0);
}

// Format phone number
export function formatPhone(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

/**
 * Synthetic domains minted for drivers who never gave us an address (the Turo importer's
 * "dion..turo@none.com", the native-sync importer's "imported.local"). The backend refuses to send
 * to them -- silently, until now -- so nothing in the UI may offer one as a recipient either.
 * Mirrors EmailUtils.PlaceholderDomains on the backend; keep the two lists in step.
 */
export const PLACEHOLDER_EMAIL_DOMAINS = ['none.com', 'imported.local'];

/**
 * The form to compare and look up by: padding and invisible characters stripped, lower-cased.
 * An address is the same address whatever its case and whatever whitespace a paste dragged along,
 * so every comparison goes through here. Mirrors EmailUtils.ForCompare on the backend.
 */
export function emailKey(email?: string | null): string {
  return (email || '').replace(/[\u00A0\u200B-\u200D\u2060\uFEFF]/g, '').trim().toLowerCase();
}

/** Do these two mean the same mailbox? Two blanks are NOT the same. */
export function isSameEmail(a?: string | null, b?: string | null): boolean {
  const left = emailKey(a);
  return left.length > 0 && left === emailKey(b);
}

/** True for an address on a synthetic placeholder domain -- a stored value, never a mailbox. */
export function isPlaceholderEmail(email?: string | null): boolean {
  const at = (email || '').lastIndexOf('@');
  if (at < 0) return false;
  return PLACEHOLDER_EMAIL_DOMAINS.includes(email!.slice(at + 1).trim().toLowerCase());
}

/**
 * Validate email. Stricter than a bare "something@something.tld": the local part is checked as RFC
 * dot-atom, so the shapes MimeKit rejects on the backend are rejected here too. The one that reached
 * production was "dion..turo@none.com" -- a doubled dot, which the old regex happily accepted and
 * the mail library then threw "Invalid local-part at offset 0" on, in front of the owner.
 */
export function isValidEmail(email: string): boolean {
  const value = (email || '').trim();
  if (!value || /\s/.test(value)) return false;
  const at = value.lastIndexOf('@');
  if (at <= 0 || at === value.length - 1) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (local.includes('@')) return false;
  // dot-atom: no leading, trailing or doubled dot, and only the characters RFC 5322 allows unquoted.
  if (!/^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/.test(local)) return false;
  // domain: dot-separated labels, each alphanumeric with inner hyphens, TLD at least two letters.
  return /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/.test(domain);
}

// Validate phone
export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10;
}

/**
 * Normalize a phone to E.164 — the same convention the backend's PhoneUtils.Normalize uses, so a
 * number typed here matches the stored one. Strips formatting, keeps the country code, never
 * truncates: bare 10 digits → +1…, 11 digits starting with 1 → +1…, anything already carrying a
 * country code (leading + or longer) keeps it. Returns '' when there are no digits.
 *
 * Use this for anything that LEAVES the browser (SMS recipient, saved contact, API payload).
 * formatPhone() above is display-only and must not be sent anywhere.
 */
export function normalizePhone(phone?: string | null): string {
  if (!phone) return '';
  const hasPlus = phone.trimStart().startsWith('+');
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  if (!hasPlus) {
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  }
  return `+${digits}`;
}

/** Last 10 digits — the subscriber-identifying part whatever the prefix or formatting. */
export function phoneLast10(phone?: string | null): string {
  const digits = (phone || '').replace(/\D/g, '');
  return digits ? digits.slice(-10) : '';
}

/**
 * True when two phones denote the same subscriber. Compares the normalized form, then the last 10
 * digits so a legacy value saved as "(201) 724-6644" still matches "+12017246644".
 */
export function isSamePhone(a?: string | null, b?: string | null): boolean {
  const na = normalizePhone(a);
  const nb = normalizePhone(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const la = phoneLast10(na);
  return la.length === 10 && la === phoneLast10(nb);
}

// Generate unique ID
export function generateId(): string {
  return `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Truncate text
export function truncate(text: string, length: number = 50): string {
  if (!text || text.length <= length) return text;
  return text.slice(0, length) + '...';
}

// Get initials from name
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
}

// Storage utilities
export const storage = {
  get: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },
  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      console.error('Error saving to localStorage');
    }
  },
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      console.error('Error removing from localStorage');
    }
  },
};

// Colors for car exterior/interior
export const ExteriorColors = [
  'Gray',
  'Black',
  'White',
  'Blue',
  'Red',
  'Silver',
  'Orange',
  'Green',
  'Bronze',
  'Yellow',
];

export const InteriorColors = ['Gray', 'Black', 'White'];

// US States
export const USStates = [
  { abbreviation: 'AL', name: 'Alabama' },
  { abbreviation: 'AK', name: 'Alaska' },
  { abbreviation: 'AZ', name: 'Arizona' },
  { abbreviation: 'AR', name: 'Arkansas' },
  { abbreviation: 'CA', name: 'California' },
  { abbreviation: 'CO', name: 'Colorado' },
  { abbreviation: 'CT', name: 'Connecticut' },
  { abbreviation: 'DE', name: 'Delaware' },
  { abbreviation: 'FL', name: 'Florida' },
  { abbreviation: 'GA', name: 'Georgia' },
  { abbreviation: 'HI', name: 'Hawaii' },
  { abbreviation: 'ID', name: 'Idaho' },
  { abbreviation: 'IL', name: 'Illinois' },
  { abbreviation: 'IN', name: 'Indiana' },
  { abbreviation: 'IA', name: 'Iowa' },
  { abbreviation: 'KS', name: 'Kansas' },
  { abbreviation: 'KY', name: 'Kentucky' },
  { abbreviation: 'LA', name: 'Louisiana' },
  { abbreviation: 'ME', name: 'Maine' },
  { abbreviation: 'MD', name: 'Maryland' },
  { abbreviation: 'MA', name: 'Massachusetts' },
  { abbreviation: 'MI', name: 'Michigan' },
  { abbreviation: 'MN', name: 'Minnesota' },
  { abbreviation: 'MS', name: 'Mississippi' },
  { abbreviation: 'MO', name: 'Missouri' },
  { abbreviation: 'MT', name: 'Montana' },
  { abbreviation: 'NE', name: 'Nebraska' },
  { abbreviation: 'NV', name: 'Nevada' },
  { abbreviation: 'NH', name: 'New Hampshire' },
  { abbreviation: 'NJ', name: 'New Jersey' },
  { abbreviation: 'NM', name: 'New Mexico' },
  { abbreviation: 'NY', name: 'New York' },
  { abbreviation: 'NC', name: 'North Carolina' },
  { abbreviation: 'ND', name: 'North Dakota' },
  { abbreviation: 'OH', name: 'Ohio' },
  { abbreviation: 'OK', name: 'Oklahoma' },
  { abbreviation: 'OR', name: 'Oregon' },
  { abbreviation: 'PA', name: 'Pennsylvania' },
  { abbreviation: 'RI', name: 'Rhode Island' },
  { abbreviation: 'SC', name: 'South Carolina' },
  { abbreviation: 'SD', name: 'South Dakota' },
  { abbreviation: 'TN', name: 'Tennessee' },
  { abbreviation: 'TX', name: 'Texas' },
  { abbreviation: 'UT', name: 'Utah' },
  { abbreviation: 'VT', name: 'Vermont' },
  { abbreviation: 'VA', name: 'Virginia' },
  { abbreviation: 'WA', name: 'Washington' },
  { abbreviation: 'WV', name: 'West Virginia' },
  { abbreviation: 'WI', name: 'Wisconsin' },
  { abbreviation: 'WY', name: 'Wyoming' },
];

// Booking Status configuration
export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'default';

export interface BookingStatusConfig {
  label: string;
  variant: BadgeVariant;
}

const bookingStatusMap: Record<number, BookingStatusConfig> = {
  [BookingStatus.Init]: { label: 'Init', variant: 'default' },
  [BookingStatus.PendingApproval]: { label: 'Pending', variant: 'warning' }, // Also covers Pending (alias)
  [BookingStatus.CompletedEarly]: { label: 'Completed Early', variant: 'info' },
  [BookingStatus.RequestExpired]: { label: 'Expired', variant: 'danger' },
  [BookingStatus.RequestDenied]: { label: 'Request Denied', variant: 'danger' },
  [BookingStatus.ApprovedWaitingForInsuranceApproval]: { label: 'Waiting Insurance', variant: 'warning' },
  [BookingStatus.Approved]: { label: 'Approved', variant: 'success' },
  [BookingStatus.Reserved]: { label: 'Reserved', variant: 'info' },
  [BookingStatus.Completed]: { label: 'Completed', variant: 'info' },
  [BookingStatus.Draft]: { label: 'Draft', variant: 'default' },
  [BookingStatus.DepositFailed]: { label: 'Deposit Failed', variant: 'danger' },
  [BookingStatus.PaymentFailed]: { label: 'Payment Failed', variant: 'danger' },
  [BookingStatus.RefundFailed]: { label: 'Refund Failed', variant: 'danger' },
  [BookingStatus.TollCardMissing]: { label: 'Toll Card Missing', variant: 'warning' },
  [BookingStatus.NotBooked]: { label: 'Not Booked', variant: 'default' },
  [BookingStatus.WaitingNewDriverInfo]: { label: 'Waiting Driver Info', variant: 'warning' },
  [BookingStatus.WaitingForAgreement]: { label: 'Waiting Agreement', variant: 'warning' },
  [BookingStatus.WaitingForCC]: { label: 'Waiting for Card', variant: 'warning' },
  [BookingStatus.Canceled]: { label: 'Cancelled', variant: 'danger' },
};

export function getBookingStatusConfig(status?: BookingStatus): BookingStatusConfig {
  if (status === undefined || status === null) {
    return { label: '-', variant: 'default' };
  }
  return bookingStatusMap[status] || { label: 'Unknown', variant: 'default' };
}

// Re-export AES utilities
export { aesEncrypt, aesDecrypt } from './aes';

// Re-export account display-name utilities
export { accountDisplayName, accountInitials } from './displayName';
export { saveReturnFilters, takeReturnFilters } from './returnFilters';
export type { NamedAccount } from './displayName';
